import { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertCircle,
  ExternalLink,
  Clock,
} from "lucide-react";
import { C, labelStyle } from "../../theme/tokens";
import { SectionHeader, TimeframeSelect, PrimaryButton } from "../../components/common/primitives";
import { PairPickerPanel } from "./PairPickerPanel";
import { formatPairLabelSafe } from "../../data/pairs";

const TIMEFRAMES = ["S5", "M2", "M5", "M10"];

/* POST /bot/signal/ { asset, deal_type } -> a signal. Selecting the
   pair/timeframe is local to this screen (no need to survive a
   layout resize); the active in-flight signal itself is owned by
   App() via useActiveSignal() and rendered as ActiveSignalOverlay —
   see hooks/useActiveSignal.js for why. */
export function SignalsScreen({ t, wide, activeSignal }) {
  const [pair, setPair] = useState("EURUSD_otc");
  const [timeframe, setTimeframe] = useState("M2");

  const entry = activeSignal?.entry;
  const signal = entry?.signal;
  const hasActive = Boolean(entry);

  const handleGetSignal = () => {
    if (hasActive || activeSignal.submitting) return;
    activeSignal.requestSignal(pair, timeframe);
  };

  const activeAsset = signal?.asset || entry?.requestedPair;
  const isOtc = (activeAsset || "").endsWith("_otc");
  const isUp = signal?.direction === "UP";
  const activeEntryPrice = signal?.entry_price ?? signal?.price ?? "—";
  const activeTf = signal?.expiration || signal?.timeframe || timeframe;

  return (
    <div>
      <SectionHeader eyebrow="On-demand" title={t.sig_title} sub={t.sig_sub} wide={wide} />
      <div
        style={{
          padding: wide ? "4px 0 20px" : "4px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          maxWidth: wide ? 520 : undefined,
          margin: wide ? "0 auto" : undefined,
          width: "100%",
        }}
      >
        {/* Active Signal Card (if signal in flight) */}
        {hasActive && (
          <div
            style={{
              borderRadius: 18,
              background: `linear-gradient(165deg, ${C.panel2} 0%, ${C.panel1} 100%)`,
              border: `1px solid ${isUp ? C.greenBright : "#C4604A"}55`,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${isUp ? C.greenBright : "#C4604A"}15`,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Header: badge + pair + direction */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: C.greenBright,
                    background: `${C.greenBright}18`,
                    border: `1px solid ${C.greenBright}44`,
                    padding: "3px 8px",
                    borderRadius: 8,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: C.greenBright,
                      boxShadow: `0 0 6px ${C.greenBright}`,
                    }}
                  />
                  {t.sig_active_badge || "Активний сигнал"}
                </span>
                <span
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 16,
                    color: C.primary,
                  }}
                >
                  {formatPairLabelSafe(activeAsset).replace(" OTC", "")}
                </span>
                {isOtc && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.violetBright,
                      background: `${C.violetBright}26`,
                      border: `1px solid ${C.violetBright}4D`,
                      padding: "1px 6px",
                      borderRadius: 6,
                    }}
                  >
                    OTC
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    fontWeight: 700,
                    color: isUp ? C.greenBright : "#EF4444",
                    background: isUp ? `${C.greenBright}18` : "rgba(239, 68, 68, 0.15)",
                    border: `1px solid ${isUp ? C.greenBright : "#EF4444"}55`,
                    padding: "3px 9px",
                    borderRadius: 8,
                  }}
                >
                  {isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {isUp ? t.sig_direction_up : t.sig_direction_down}
                </span>
                {activeTf && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 600,
                      color: C.secondary,
                      background: C.panel1,
                      border: `1px solid ${C.hairline}`,
                      padding: "3px 7px",
                      borderRadius: 8,
                    }}
                  >
                    <Clock size={11} color={C.mutedB} />
                    {activeTf}
                  </span>
                )}
              </div>
            </div>

            {/* Binary signal entry price */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 8,
                background: C.panel1,
                border: `1px solid ${C.hairline}`,
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: C.mutedB, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                  {t.sig_field_price || "Ціна входу"}
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: C.primary, marginTop: 2 }}>
                  {activeEntryPrice}
                </div>
              </div>
            </div>

            {/* Actions: View Details (opens overlay) + Result buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                onClick={() => activeSignal.openOverlay()}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${C.hairline}`,
                  background: C.panel1,
                  color: C.primary,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <ExternalLink size={14} color={C.greenLight} />
                {t.sig_view_details || "Переглянути сетап"}
              </button>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => activeSignal.submitResult(true)}
                  disabled={activeSignal.resultSubmitting}
                  style={{
                    flex: 1,
                    padding: "10px 8px",
                    borderRadius: 10,
                    border: "none",
                    background: activeSignal.resultSubmitting
                      ? C.mutedA
                      : `linear-gradient(120deg, ${C.greenBright} 0%, ${C.green} 100%)`,
                    color: activeSignal.resultSubmitting ? C.mutedB : "#0b0d13",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 12.5,
                    cursor: activeSignal.resultSubmitting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                  }}
                >
                  {t.sig_btn_profit || "Угода в плюс"}
                </button>

                <button
                  type="button"
                  onClick={() => activeSignal.submitResult(false)}
                  disabled={activeSignal.resultSubmitting}
                  style={{
                    flex: 1,
                    padding: "10px 8px",
                    borderRadius: 10,
                    border: `1px solid ${activeSignal.resultSubmitting ? C.hairline : "#EF4444"}`,
                    background: "rgba(239, 68, 68, 0.08)",
                    color: activeSignal.resultSubmitting ? C.mutedB : "#EF4444",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 12.5,
                    cursor: activeSignal.resultSubmitting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                  }}
                >
                  {t.sig_btn_loss || "Угода в мінус"}
                </button>

                <button
                  type="button"
                  onClick={() => activeSignal.cancelSignal()}
                  disabled={activeSignal.resultSubmitting}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${C.hairline}`,
                    background: "transparent",
                    color: C.secondary,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: 12.5,
                    cursor: activeSignal.resultSubmitting ? "not-allowed" : "pointer",
                  }}
                  title={t.sig_btn_no_trade || "Не входив у угоду"}
                >
                  ✕
                </button>
              </div>

              {activeSignal.resultError && (
                <div style={{ color: "#C4604A", fontSize: 12, textAlign: "center" }}>
                  {t.sig_result_error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Warning if signal active */}
        {hasActive && (
          <div
            style={{
              borderRadius: 14,
              background: "rgba(234, 179, 8, 0.08)",
              border: "1px solid rgba(234, 179, 8, 0.25)",
              padding: "12px 14px",
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <AlertCircle size={17} color="#EAB308" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12.5, color: "#FACC15", lineHeight: 1.45 }}>
              {t.sig_active_warning}
            </div>
          </div>
        )}

        {/* Inputs (dimmed/disabled when active signal exists) */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
            opacity: hasActive ? 0.45 : 1,
            pointerEvents: hasActive ? "none" : "auto",
            userSelect: hasActive ? "none" : "auto",
            transition: "opacity .2s ease",
          }}
        >
          <div>
            <div style={labelStyle}>{t.sig_pair}</div>
            <PairPickerPanel
              value={pair}
              onChange={setPair}
              forexLabel={t.sig_pair_group_forex}
              otcLabel={t.sig_pair_group_otc}
            />
          </div>
          <div>
            <div style={labelStyle}>{t.sig_type}</div>
            <TimeframeSelect value={timeframe} onChange={setTimeframe} options={TIMEFRAMES} accent={C.violetBright} />
          </div>
        </div>

        {!hasActive && (
          <div
            style={{
              background: C.panel1,
              border: `1px solid ${C.hairline}`,
              borderRadius: 16,
              padding: 16,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${C.violetDeep} 0%, ${C.violet} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <TrendingUp size={18} color={C.violetBright} />
            </div>
            <div style={{ fontSize: 12.5, color: C.mutedB, lineHeight: 1.4 }}>{t.sig_note}</div>
          </div>
        )}

        {activeSignal.error && (
          <div style={{ color: "#C4604A", fontSize: 12.5, textAlign: "center" }}>
            {activeSignal.error === "trial_limit" ? t.sig_error_trial_limit : t.sig_error}
          </div>
        )}

        <PrimaryButton onClick={handleGetSignal} disabled={activeSignal.submitting || hasActive}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%" }}>
            {activeSignal.submitting ? <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} /> : null}
            {hasActive ? t.sig_active_must_close : t.sig_cta}
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
}